/**
 * Bob Stage Pack
 *
 * Creates Bob (Runescape cat) meshes with physics and theme integration.
 */

import { Mesh, Vector3 } from "@babylonjs/core";
import {
  BobManager,
  EventBus,
  type StageConfigManager,
  type StagePack,
  type StageThemeInfo,
} from "@tony/nl-babylon";
import type { Theme } from "@/context/ThemeContext";

/**
 * GLB configuration for the bob model (single variant).
 * Used by Three.js and model-viewer alternative viewers.
 */
export const BOB_GLB_CONFIGS: Record<
  string,
  { glbFile?: string; glbPath?: string }
> = {
  default: {},
};

/** Default GLB key when none specified */
export const DEFAULT_BOB_GLB_KEY = "default";

/**
 * Default bob configuration
 */
const DEFAULT_BOB_CONFIG = {
  count: 1,
  scale: 1,
};

/**
 * Bob stage pack definition
 */
/** Theme-update manager keyed by the meshes array returned from
    createAssets. applyTheme falls back to a temporary manager when called
    with a different array (e.g. the combined stage array after insertPack). */
const bobManagers = new WeakMap<Mesh[], BobManager>();

export const bobStagePack: StagePack = {
  applyTheme: (meshes, theme: StageThemeInfo) => {
    // Get the stored bobManager
    const bobManager = bobManagers.get(meshes);
    if (!bobManager) {
      // Fallback: create a temporary manager for theme application
      const eventBus = new EventBus();
      const tempManager = new BobManager(eventBus);
      tempManager.applyThemeColors(
        meshes,
        theme.themeId as Theme,
        theme.isDarkMode,
      );
      return;
    }

    // Collect all bob meshes including children for theme application
    const allMeshes: Mesh[] = [];
    for (const rootMesh of meshes) {
      allMeshes.push(rootMesh);
      for (const child of rootMesh.getChildMeshes()) {
        if (child instanceof Mesh) {
          allMeshes.push(child);
        }
      }
    }
    bobManager.applyThemeColors(
      allMeshes,
      theme.themeId as Theme,
      theme.isDarkMode,
    );
  },

  createAssets: async ({ physicsManager, scene }) => {
    // Read config from StageConfigManager (set by StageContext) or use defaults
    const stageConfigManager = (
      scene.metadata as { stageConfigManager?: StageConfigManager } | undefined
    )?.stageConfigManager;
    const stageConfig = stageConfigManager?.config;
    const config = {
      count: stageConfig?.count ?? DEFAULT_BOB_CONFIG.count,
      scale: stageConfig?.scale ?? DEFAULT_BOB_CONFIG.scale,
    };

    console.log("[BobStagePack] Creating with config:", config);

    const eventBus = new EventBus();
    const bobManager = new BobManager(eventBus);
    await bobManager.initialize();
    bobManager.setScene(scene);

    const meshes: Mesh[] = [];

    for (let i = 0; i < config.count; i++) {
      // Create bob using BobManager
      const bobMeshes = bobManager.createBob(scene, {
        scale: config.scale,
      });

      // Position: center for single bob, circle for multiple
      const startY = 0.5 + Math.random() * 0.5;
      let startX = 0;
      let startZ = 0;

      if (config.count > 1) {
        const angle = (i / config.count) * Math.PI * 2;
        const radius = 3 + Math.random() * 2;
        startX = Math.cos(angle) * radius;
        startZ = Math.sin(angle) * radius;
      }

      // Find the root mesh (first mesh is the parent)
      const rootMesh = bobMeshes[0];
      if (rootMesh) {
        rootMesh.position.set(startX, startY, startZ);
        rootMesh.rotation.y = Math.random() * Math.PI * 2;

        // Mark as physics root for InteractionManager
        rootMesh.metadata = { ...rootMesh.metadata, isPhysicsRoot: true };

        // Add physics body - CONVEX_HULL for complex cat geometry
        physicsManager.addPhysicsBody(rootMesh, {
          angularDamping: 0.8,
          friction: 0.6,
          linearDamping: 0.5,
          mass: 3.0, // Heavier than gems - feels like a solid cat
          restitution: 0.2,
          shapeType: "CONVEX_HULL",
        });

        meshes.push(rootMesh);
      }
    }

    // Store bobManager reference for theme updates
    bobManagers.set(meshes, bobManager);

    return meshes;
  },
  displayName: "Bob the Cat",
  id: "bob",

  interactionConfig: {
    hoverEffects: true,
    selectionEffects: true,
  },

  physicsConfig: {
    bodyOptions: {
      angularDamping: 0.8,
      friction: 0.6,
      linearDamping: 0.5,
      mass: 3.0,
      restitution: 0.2,
      shapeType: "CONVEX_HULL",
    },
    gravity: new Vector3(0, -9.8, 0),
  },
};
